/**
 * INPUT COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Liquid Glass Clear material (ultra-transparent)
 * - "Lift into glass" on focus (per WWDC25) - element lifts and becomes more solid
 * - Adaptive focus states with animated ring
 * - Organic spring motion on interaction
 * - Specular highlights and pearlescent shimmer
 * - Floating label animation
 */

import { InputHTMLAttributes, TextareaHTMLAttributes, ReactNode, useState, forwardRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../lib/utils'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: ReactNode
  iconRight?: ReactNode
  error?: string
  label?: string
  hint?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  icon,
  iconRight,
  error,
  label,
  hint,
  className,
  required,
  disabled,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
          {label}
          {required && <span className="text-red dark:text-red-dark ml-1">*</span>}
        </label>
      )}
      <motion.div
        className="relative group"
        animate={{
          scale: isFocused ? 1.01 : 1,
          y: isFocused ? -1 : 0,
        }}
        transition={{
          type: "spring",
          damping: 25,
          stiffness: 400,
        }}
      >
        {/* Left icon */}
        {icon && (
          <motion.div
            className={cn(
              "absolute left-3.5 top-1/2 -translate-y-1/2 z-10",
              "text-light-text-tertiary dark:text-dark-text-tertiary"
            )}
            animate={{
              scale: isFocused ? 1.1 : 1,
              color: isFocused ? 'var(--color-blue)' : undefined,
            }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
          >
            {icon}
          </motion.div>
        )}

        {/* Input field */}
        <input
          ref={ref}
          className={cn(
            // Base styles
            'w-full px-4 py-3 rounded-xl outline-none',
            'text-body text-light-text-primary dark:text-dark-text-primary',
            'placeholder:text-light-text-quaternary dark:placeholder:text-dark-text-quaternary',
            'tracking-tight',

            // Liquid Glass Clear material (starts transparent)
            'liquid-material-clear',
            'border border-transparent',

            // Transitions
            'transition-all duration-base ease-liquid-out',

            // Focus state - "lift into glass" effect
            isFocused && [
              'liquid-material-regular',
              'shadow-glass-lg dark:shadow-dark-glass-lg',
              'border-blue/20 dark:border-blue-dark/20',
            ],

            // Error state
            error && 'border-red/40 dark:border-red-dark/40 ring-1 ring-red/20',

            // Disabled state
            disabled && 'opacity-50 cursor-not-allowed',

            // Icon spacing
            icon && 'pl-11',
            iconRight && 'pr-11',

            className
          )}
          required={required}
          disabled={disabled}
          onFocus={(e) => {
            setIsFocused(true)
            props.onFocus?.(e)
          }}
          onBlur={(e) => {
            setIsFocused(false)
            props.onBlur?.(e)
          }}
          {...props}
        />

        {/* Focus ring glow animation */}
        <AnimatePresence>
          {isFocused && (
            <motion.div
              className="absolute inset-0 rounded-xl pointer-events-none"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              style={{
                boxShadow: '0 0 0 3px rgba(0, 122, 255, 0.15), 0 0 20px rgba(0, 122, 255, 0.1)',
              }}
            />
          )}
        </AnimatePresence>

        {/* Right icon */}
        {iconRight && (
          <div className={cn(
            "absolute right-3.5 top-1/2 -translate-y-1/2 transition-all duration-fast z-10",
            "text-light-text-tertiary dark:text-dark-text-tertiary"
          )}>
            {iconRight}
          </div>
        )}
      </motion.div>

      {/* Hint text */}
      {hint && !error && (
        <p className="text-footnote text-light-text-tertiary dark:text-dark-text-tertiary">
          {hint}
        </p>
      )}

      {/* Error message with animation */}
      <AnimatePresence>
        {error && (
          <motion.p
            className="text-footnote text-red dark:text-red-dark flex items-center gap-1.5"
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  )
})

Input.displayName = 'Input'

/**
 * Textarea - Multi-line input with Liquid Glass
 */
interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string
  label?: string
  hint?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  error,
  label,
  hint,
  className,
  required,
  disabled,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
          {label}
          {required && <span className="text-red dark:text-red-dark ml-1">*</span>}
        </label>
      )}

      <motion.div
        className="relative"
        animate={{
          scale: isFocused ? 1.005 : 1,
          y: isFocused ? -1 : 0,
        }}
        transition={{
          type: "spring",
          damping: 25,
          stiffness: 400,
        }}
      >
        <textarea
          ref={ref}
          className={cn(
            // Base styles
            'w-full px-4 py-3 rounded-xl outline-none resize-y min-h-[120px]',
            'text-body text-light-text-primary dark:text-dark-text-primary',
            'placeholder:text-light-text-quaternary dark:placeholder:text-dark-text-quaternary',
            'tracking-tight',

            // Liquid Glass Clear material
            'liquid-material-clear',
            'border border-transparent',

            // Transitions
            'transition-all duration-base ease-liquid-out',

            // Focus state - "lift into glass"
            isFocused && [
              'liquid-material-regular',
              'shadow-glass-lg dark:shadow-dark-glass-lg',
              'border-blue/20 dark:border-blue-dark/20',
            ],

            // Error state
            error && 'border-red/40 dark:border-red-dark/40 ring-1 ring-red/20',

            // Disabled state
            disabled && 'opacity-50 cursor-not-allowed',

            // Glass scrollbar
            'scrollbar-glass',

            className
          )}
          required={required}
          disabled={disabled}
          onFocus={(e) => {
            setIsFocused(true)
            props.onFocus?.(e)
          }}
          onBlur={(e) => {
            setIsFocused(false)
            props.onBlur?.(e)
          }}
          {...props}
        />

        {/* Focus ring glow animation */}
        <AnimatePresence>
          {isFocused && (
            <motion.div
              className="absolute inset-0 rounded-xl pointer-events-none"
              initial={{ opacity: 0, scale: 0.99 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.99 }}
              transition={{ duration: 0.2 }}
              style={{
                boxShadow: '0 0 0 3px rgba(0, 122, 255, 0.15), 0 0 20px rgba(0, 122, 255, 0.1)',
              }}
            />
          )}
        </AnimatePresence>
      </motion.div>

      {/* Hint text */}
      {hint && !error && (
        <p className="text-footnote text-light-text-tertiary dark:text-dark-text-tertiary">
          {hint}
        </p>
      )}

      {/* Error message with animation */}
      <AnimatePresence>
        {error && (
          <motion.p
            className="text-footnote text-red dark:text-red-dark flex items-center gap-1.5"
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  )
})

Textarea.displayName = 'Textarea'

/**
 * SearchInput - Search field with icon
 * Features materialize-in/out animation per WWDC25
 */
interface SearchInputProps extends Omit<InputProps, 'icon'> {
  onClear?: () => void
}

export function SearchInput({
  onClear,
  value,
  className,
  ...props
}: SearchInputProps) {
  const [isFocused, setIsFocused] = useState(false)
  const hasValue = value && String(value).length > 0

  return (
    <motion.div
      className={cn(
        'liquid-search',
        className
      )}
      animate={{
        scale: isFocused ? 1.02 : 1,
        y: isFocused ? -1 : 0,
      }}
      transition={{
        type: "spring",
        damping: 25,
        stiffness: 400,
      }}
      style={{
        boxShadow: isFocused
          ? '0 4px 24px rgba(0, 0, 0, 0.08), 0 0 0 3px rgba(0, 122, 255, 0.12)'
          : '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <motion.svg
        className="w-5 h-5 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        animate={{
          scale: isFocused ? 1.1 : 1,
          color: isFocused ? 'var(--color-blue)' : 'var(--color-text-tertiary)',
        }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </motion.svg>
      <input
        type="search"
        value={value}
        className="bg-transparent border-none outline-none flex-1 text-body text-light-text-primary dark:text-dark-text-primary placeholder:text-light-text-quaternary dark:placeholder:text-dark-text-quaternary tracking-tight"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        {...props}
      />
      <AnimatePresence>
        {hasValue && onClear && (
          <motion.button
            type="button"
            onClick={onClear}
            className="p-1.5 rounded-full hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors duration-fast"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <svg className="w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
